import { createSlice } from "@reduxjs/toolkit";
import {
  downloadTaxes,
  getTaxesBreakdowns,
  getTaxesDaily,
  getTaxesDetails,
  getTaxesInsights,
  getTaxesInsightsBreakdowns,
  getTaxesInsightsHourly,
  getTaxesSummary,
  getTaxesTypes,
} from "./taxesV3Action";

const section = (data) => ({
  loading: false,
  error: null,
  data,
  latestRequestId: null,
});

// Shapes of apps/api/v3/taxes.py (CANA-929).
const initialState = {
  summary: section(null),
  types: section({ rows: [], total: null }),
  daily: section({ results: [], previous: null }),
  breakdowns: section({ regions: [], sources: [] }),
  details: section({ rows: [], meta: null }),
  insights: section(null),
  insightsHourly: section(null),
  insightsBreakdowns: section(null),
  download: { loading: false, error: null },
};

// Filters change faster than the sections answer: only the latest request of
// a section may write to it.
const addSectionCases = (builder, thunk, key) =>
  builder
    .addCase(thunk.pending, (state, action) => {
      state[key].loading = true;
      state[key].error = null;
      state[key].latestRequestId = action.meta.requestId;
    })
    .addCase(thunk.fulfilled, (state, action) => {
      if (action.meta.requestId !== state[key].latestRequestId) return;
      state[key].loading = false;
      state[key].data = action.payload;
    })
    .addCase(thunk.rejected, (state, action) => {
      if (action.meta.requestId !== state[key].latestRequestId) return;
      state[key].loading = false;
      state[key].data = initialState[key].data;
      state[key].error = action.payload;
    });

// The three requests of the open day's window. The answers do not carry the
// day, so a new day starts them empty and the window never shows the
// previous day's numbers.
const DAY_KEYS = ["insights", "insightsHourly", "insightsBreakdowns"];

const taxesV3 = createSlice({
  name: "taxesV3",
  initialState,
  reducers: {
    resetDayInsights: (state) => {
      DAY_KEYS.forEach((key) => {
        state[key] = section(null);
      });
    },
  },
  extraReducers: (builder) => {
    addSectionCases(builder, getTaxesSummary, "summary");
    addSectionCases(builder, getTaxesTypes, "types");
    addSectionCases(builder, getTaxesDaily, "daily");
    addSectionCases(builder, getTaxesBreakdowns, "breakdowns");
    addSectionCases(builder, getTaxesDetails, "details");
    addSectionCases(builder, getTaxesInsights, "insights");
    addSectionCases(builder, getTaxesInsightsHourly, "insightsHourly");
    addSectionCases(builder, getTaxesInsightsBreakdowns, "insightsBreakdowns");
    builder
      .addCase(downloadTaxes.pending, (state) => {
        state.download.loading = true;
        state.download.error = null;
      })
      .addCase(downloadTaxes.fulfilled, (state) => {
        state.download.loading = false;
      })
      .addCase(downloadTaxes.rejected, (state, action) => {
        state.download.loading = false;
        state.download.error = action.payload;
      });
  },
});

export const { resetDayInsights } = taxesV3.actions;
export default taxesV3.reducer;
